import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import MetaTagManager from '../components/MetaTagManager';
import MetaTagFixer from '../utils/metaTagFixer';

// Tipos para Canva Connect APIs
interface CanvaDesign {
  id: string;
  title: string;
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface CanvaAsset {
  id: string;
  name: string;
  url: string;
  type: string;
}

const StudioLab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [designs, setDesigns] = useState<CanvaDesign[]>([]);
  const [assets, setAssets] = useState<CanvaAsset[]>([]);

  // Configurações do Canva Connect APIs
  const CONFIG = {
    CLIENT_ID: import.meta.env.VITE_CANVA_CLIENT_ID || "YOUR_CANVA_CLIENT_ID",
    CLIENT_SECRET: import.meta.env.VITE_CANVA_CLIENT_SECRET || "YOUR_CANVA_CLIENT_SECRET",
    REDIRECT_URI: window.location.origin + '/studiolab/callback',
    API_BASE_URL: "https://api.canva.com",
    AUTH_URL: "https://www.canva.com/oauth/authorize",
    TOKEN_URL: "https://api.canva.com/oauth/token"
  };

  // Verificar se as credenciais estão configuradas
  const isConfigured = CONFIG.CLIENT_ID !== "YOUR_CANVA_CLIENT_ID" && 
                      CONFIG.CLIENT_SECRET !== "YOUR_CANVA_CLIENT_SECRET";

  useEffect(() => {
    console.log("🚀 Iniciando StudioLab com Canva Connect APIs...");
    console.log("🔑 Client ID:", CONFIG.CLIENT_ID);
    
    // Corrigir meta tags deprecated usando o utilitário
    MetaTagFixer.autoFix();
    
    // Capturar email do usuário logado
    captureUserEmail().catch(console.error);
    
    // Verificar se estamos no callback OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    
    if (code) {
      console.log("🔄 Processando callback OAuth...");
      exchangeCodeForToken(code)
        .then(() => {
          console.log("✅ Autenticação OAuth concluída");
          // Limpar URL após autenticação
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((err) => {
          console.error("❌ Erro na autenticação OAuth:", err);
          setError(`Erro na autenticação: ${err.message}`);
        });
    } else if (error) {
      console.error("❌ Erro no callback OAuth:", error);
      setError(`Erro de autorização: ${error}`);
    } else {
      // Verificar se já temos um token válido
      const storedToken = localStorage.getItem('canva_access_token');
      if (storedToken) {
        setAccessToken(storedToken);
        setIsAuthenticated(true);
        loadUserData(storedToken);
      }
    }
  }, []);

  // Função para corrigir meta tags deprecated
  const fixDeprecatedMetaTags = () => {
    try {
      // Remover meta tag deprecated do Apple
      const deprecatedMeta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
      if (deprecatedMeta) {
        deprecatedMeta.remove();
        console.log("✅ Meta tag deprecated 'apple-mobile-web-app-capable' removida");
      }

      // Adicionar meta tag correta se não existir
      const correctMeta = document.querySelector('meta[name="mobile-web-app-capable"]');
      if (!correctMeta) {
        const newMeta = document.createElement('meta');
        newMeta.name = 'mobile-web-app-capable';
        newMeta.content = 'yes';
        document.head.appendChild(newMeta);
        console.log("✅ Meta tag correta 'mobile-web-app-capable' adicionada");
      } else {
        console.log("✅ Meta tag 'mobile-web-app-capable' já existe");
      }

      // Verificar se há outras meta tags deprecated relacionadas
      const otherDeprecatedTags = [
        'apple-mobile-web-app-status-bar-style',
        'apple-mobile-web-app-title'
      ];

      otherDeprecatedTags.forEach(tagName => {
        const tag = document.querySelector(`meta[name="${tagName}"]`);
        if (tag) {
          tag.remove();
          console.log(`✅ Meta tag deprecated '${tagName}' removida`);
        }
      });

    } catch (error) {
      console.error("❌ Erro ao corrigir meta tags:", error);
    }
  };

  // Função para capturar email do usuário logado
  const captureUserEmail = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        localStorage.setItem('user_email', user.email);
        console.log("📧 Email do usuário capturado:", user.email);
      }
    } catch (error) {
      console.log("ℹ️ Não foi possível capturar email do usuário");
    }
  };

  // Função para iniciar autenticação OAuth 2.0
  const startOAuthFlow = async () => {
    const state = Math.random().toString(36).substring(7);
    localStorage.setItem('canva_oauth_state', state);
    
    const userEmail = localStorage.getItem('user_email') || '';
    
    const authUrl = new URL(CONFIG.AUTH_URL);
    authUrl.searchParams.set('client_id', CONFIG.CLIENT_ID);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('redirect_uri', CONFIG.REDIRECT_URI);
    authUrl.searchParams.set('scope', 'user.profile:read designs:read designs:write assets:read assets:write');
    authUrl.searchParams.set('state', state);
    
    if (userEmail) {
      authUrl.searchParams.set('login_hint', userEmail);
      console.log("🔐 Iniciando fluxo OAuth 2.0 com email:", userEmail);
    } else {
      console.log("🔐 Iniciando fluxo OAuth 2.0...");
    }
    
    console.log("🔗 URL de autorização:", authUrl.toString());
    window.location.href = authUrl.toString();
  };

  // Função para trocar código por token
  const exchangeCodeForToken = async (code: string) => {
    const state = localStorage.getItem('canva_oauth_state');
    if (!state) {
      throw new Error('State não encontrado');
    }

    const tokenResponse = await fetch(CONFIG.TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${btoa(`${CONFIG.CLIENT_ID}:${CONFIG.CLIENT_SECRET}`)}`
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: CONFIG.REDIRECT_URI,
        state: state
      }),
    });

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(`Erro ao obter token: ${errorData.error || tokenResponse.statusText}`);
    }

    const tokenData = await tokenResponse.json();
    console.log("✅ Token obtido com sucesso");
    
    setAccessToken(tokenData.access_token);
    setIsAuthenticated(true);
    localStorage.setItem('canva_access_token', tokenData.access_token);
    localStorage.removeItem('canva_oauth_state');
    
    // Carregar dados do usuário
    await loadUserData(tokenData.access_token);
    
    return tokenData.access_token;
  };

  // Função para carregar dados do usuário
  const loadUserData = async (token: string) => {
    try {
      setLoading(true);
      
      // Carregar designs do usuário
      const designsResponse = await fetch(`${CONFIG.API_BASE_URL}/designs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (designsResponse.ok) {
        const designsData = await designsResponse.json();
        setDesigns(designsData.designs || []);
        console.log("✅ Designs carregados:", designsData.designs?.length || 0);
      }

      // Carregar assets do usuário
      const assetsResponse = await fetch(`${CONFIG.API_BASE_URL}/assets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (assetsResponse.ok) {
        const assetsData = await assetsResponse.json();
        setAssets(assetsData.assets || []);
        console.log("✅ Assets carregados:", assetsData.assets?.length || 0);
      }

    } catch (error) {
      console.error("❌ Erro ao carregar dados:", error);
      setError("Erro ao carregar dados do usuário");
    } finally {
      setLoading(false);
    }
  };

  // Função para criar novo design
  const createNewDesign = async () => {
    if (!accessToken) {
      setError("Você precisa estar autenticado");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${CONFIG.API_BASE_URL}/designs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `Design ${new Date().toLocaleDateString()}`,
          brand_kit_id: null, // Opcional
          dimensions: {
            width: 1920,
            height: 1080
          }
        })
      });

      if (response.ok) {
        const design = await response.json();
        console.log("✅ Design criado:", design);
        
        // Abrir o design no Canva
        window.open(`https://www.canva.com/design/${design.id}`, '_blank');
        
        // Recarregar lista de designs
        await loadUserData(accessToken);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao criar design');
      }
    } catch (error) {
      console.error("❌ Erro ao criar design:", error);
      setError(`Erro ao criar design: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir design existente
  const openDesign = (designId: string) => {
    window.open(`https://www.canva.com/design/${designId}`, '_blank');
  };

  // Função para fazer logout
  const logout = () => {
    setAccessToken(null);
    setIsAuthenticated(false);
    setDesigns([]);
    setAssets([]);
    localStorage.removeItem('canva_access_token');
    localStorage.removeItem('canva_oauth_state');
    console.log("✅ Logout realizado");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">StudioLab</h1>
              <p className="text-gray-600">Criação de Anúncios com Canva</p>
            </div>
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <button
                  onClick={logout}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Sair
                </button>
              ) : (
                <button
                  onClick={startOAuthFlow}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Conectar com Canva
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Crie Anúncios Profissionais com Canva
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Utilize o poder do Canva para criar campanhas de mídia indoor impactantes e profissionais.
            Acesse milhares de templates e ferramentas de design.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Authentication Section */}
        {!isAuthenticated ? (
          <div className="space-y-8">
            {/* Meta Tag Manager */}
            <div className="flex justify-center">
              <MetaTagManager />
            </div>
            
            {/* Configuration Check */}
            {!isConfigured ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <svg className="h-6 w-6 text-yellow-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h3 className="text-lg font-medium text-yellow-800">
                    Configuração Necessária
                  </h3>
                </div>
                <div className="text-yellow-700">
                  <p className="mb-4">
                    As credenciais do Canva Connect não estão configuradas. Para usar o StudioLab, você precisa:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 mb-4">
                    <li>Acessar <a href="https://www.canva.dev/portal/apps" target="_blank" rel="noopener noreferrer" className="underline font-medium">Canva Developer Portal</a></li>
                    <li>Criar uma nova aplicação ou usar uma existente</li>
                    <li>Copiar o Client ID e Client Secret</li>
                    <li>Configurar o arquivo .env</li>
                  </ol>
                  <div className="bg-white p-4 rounded border">
                    <p className="text-sm font-mono text-gray-800 mb-2">Execute no terminal:</p>
                    <code className="text-sm bg-gray-100 p-2 rounded block">
                      node setup-canva-config.js
                    </code>
                  </div>
                </div>
              </div>
            ) : (
              /* Authentication Form */
              <div className="text-center py-12">
                <div className="max-w-md mx-auto">
                  <div className="mb-6">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Conecte-se ao Canva
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Para começar a criar designs, você precisa conectar sua conta do Canva.
                  </p>
                  <button
                    onClick={startOAuthFlow}
                    disabled={loading}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-md"
                  >
                    {loading ? 'Conectando...' : 'Conectar com Canva'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Authenticated Content */
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Ações Rápidas</h3>
              <div className="flex space-x-4">
                <button
                  onClick={createNewDesign}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-md"
                >
                  {loading ? 'Criando...' : 'Criar Novo Design'}
                </button>
                <button
                  onClick={() => loadUserData(accessToken!)}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-400 rounded-md"
                >
                  {loading ? 'Atualizando...' : 'Atualizar Lista'}
                </button>
              </div>
            </div>

            {/* Designs Section */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Meus Designs</h3>
              </div>
              <div className="p-6">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Carregando designs...</p>
                  </div>
                ) : designs.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {designs.map((design) => (
                      <div
                        key={design.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => openDesign(design.id)}
                      >
                        {design.thumbnailUrl && (
                          <img
                            src={design.thumbnailUrl}
                            alt={design.title}
                            className="w-full h-32 object-cover rounded-md mb-3"
                          />
                        )}
                        <h4 className="font-medium text-gray-900 mb-1">{design.title}</h4>
                        <p className="text-sm text-gray-500">
                          Criado em {new Date(design.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Nenhum design encontrado.</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Crie seu primeiro design clicando em "Criar Novo Design".
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Assets Section */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Meus Assets</h3>
              </div>
              <div className="p-6">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Carregando assets...</p>
                  </div>
                ) : assets.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {assets.map((asset) => (
                      <div key={asset.id} className="border border-gray-200 rounded-lg p-4">
                        <img
                          src={asset.url}
                          alt={asset.name}
                          className="w-full h-32 object-cover rounded-md mb-3"
                        />
                        <h4 className="font-medium text-gray-900 mb-1">{asset.name}</h4>
                        <p className="text-sm text-gray-500">{asset.type}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Nenhum asset encontrado.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StudioLab; 